<script setup lang="ts">
import { substituteName, SubstituteInit } from './tinymce/plugins/substitute/plugin';
import { shortcodeName, ShortCodeInit } from './tinymce/plugins/shortcode/plugin';
import ShortcodesPlugin from './plugins/ShortcodesPlugin.vue';
import TimelinePlugin from './plugins/TimelinePlugin.vue';
import InternalLinksPlugin from './plugins/InternalLinksPlugin.vue';
import PremiumPlugin from './plugins/PremiumPlugin.vue';
import PagesPlugin from './plugins/PagesPlugin.vue';
import contentStyle from './styles.css?inline';
import RichTextEditor from '#layers/aicore/app/components/edito/editor/RichTextEditor.vue';

const model = defineModel<string>({ default: '' });
const props = withDefaults(defineProps<{
    id: string;
    name?: string;
    disabled?: boolean;
    setup?: (editor: any) => void;
    options?: (options: any) => any;
    templates?: (templates: any[]) => any[];
    shortcode?: boolean;
    timeline?: boolean;
    premium?: boolean;
    customLinks?: boolean;
}>(), {
    name: undefined,
    disabled: false,
    setup: (editor: any) => {},
    options: (options: any) => options,
    templates: (templates: any[]) => templates,
    shortcode: false,
    timeline: false,
    premium: false,
    customLinks: false
});

const emit = defineEmits<{
    (e: 'input', value: string): void;
    (e: 'onConnectLinks', ids: number[]): void;
}>();

const shortCodesPlugin = ref<InstanceType<typeof ShortcodesPlugin>>();
const timelinePlugin = ref<InstanceType<typeof TimelinePlugin>>();
const internalLinksPlugin = ref<InstanceType<typeof InternalLinksPlugin>>();
const premiumPlugin = ref<InstanceType<typeof PremiumPlugin>>();
const pagesPlugin = ref<InstanceType<typeof PagesPlugin>>();

function settingsFn(options: any)
{
    return props.options({
        ...options,
        plugins: [substituteName, shortcodeName, ...options.plugins],
        menu: {
            ...options.menu,
            insert: {
                ...options.menu.insert,
                items: `${options.menu.insert.items}${props.timeline ? ' | timeline' : ''}`
            },
            tools: {
                title: $t('[[[Narzędzia]]]'),
                items: `code wordcount | ${props.premium ? 'premium ' : ''} sc-gallery sc-youtube sc-related-articles sc-survey ${props.customLinks ? ' | connectlinks' : ''}`
            }
        },
        toolbar: `${props.shortcode ? `${shortcodeName} | ` : ''}${options.toolbar}`,
        quickbars_insert_toolbar: `${props.shortcode ? `${shortcodeName} | ` : ''} ${options.quickbars_insert_toolbar}`,
        content_style: contentStyle,
        noneditable_class: 'mceNonEditable'
    });
}

function setupFn(editor: any)
{
    // Natywne plugins
    SubstituteInit(editor.editorManager);
    ShortCodeInit(editor.editorManager);

    // Pluginy hybrydowe
    shortCodesPlugin.value.init(editor);
    timelinePlugin.value.init(editor);
    internalLinksPlugin.value.init(editor);
    premiumPlugin.value.init(editor);
    pagesPlugin.value.init(editor);

    // Zakończenie konfiguracji
    props.setup(editor);
}

function templatesFn(templates: any[])
{
    return props.templates(templates);
}
</script>

<template>
    <RichTextEditor
        :id="props.id"
        v-model="model"
        :name="props.name"
        :setup="setupFn"
        :options="settingsFn"
        :templates="templatesFn"
        @input="emit('input', $event)"
    />
    <ShortcodesPlugin ref="shortCodesPlugin" v-model="model" :enabled="props.shortcode" />
    <TimelinePlugin ref="timelinePlugin" v-model="model" :enabled="props.timeline" />
    <InternalLinksPlugin ref="internalLinksPlugin" v-model="model" @on-connect-links="emit('onConnectLinks', $event)" />
    <PremiumPlugin ref="premiumPlugin" />
    <PagesPlugin ref="pagesPlugin" />
</template>
